import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, PencilSimple, ListChecks, CheckCircle, Warning, Copy, TreeStructure, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EmptyState } from "../components/EmptyState";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils-extra";

const KPI_TYPES = [
  { value: "objective", label: "Objective (target vs actual)" },
  { value: "manager_rated", label: "Manager-Rated (0–10 scale)" },
];
const TARGET_TYPES = [
  { value: "percentage", label: "Percentage" },
  { value: "quantity", label: "Quantity" },
  { value: "currency", label: "Currency" },
  { value: "count", label: "Count" },
  { value: "rating_scale", label: "Rating Scale" },
];
const MEASUREMENTS = [
  { value: "higher_is_better", label: "Higher is better" },
  { value: "lower_is_better", label: "Lower is better" },
  { value: "completion", label: "Completion based" },
  { value: "manager_rating", label: "Manager rating" },
];
const DATA_SOURCES = [
  { value: "manual", label: "Manual entry" },
  { value: "attendance", label: "Attendance" },
  { value: "sales", label: "Sales" },
  { value: "call_tracker", label: "Call Tracker" },
  { value: "crm", label: "CRM" },
  { value: "finance", label: "Finance" },
  { value: "inventory", label: "Inventory" },
  { value: "future", label: "Future integration" },
];

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const emptyLine = () => ({
  id: uid(), name: "", description: "", category: "General",
  kpi_type: "objective", weight: 0, target_type: "percentage",
  measurement: "higher_is_better", data_source: "manual", default_target: null, active: true,
  has_subcategories: false, subcategories: [],
});
const emptyKRA = () => ({
  id: uid(), statement: "", weight: 0,
  enable_objective: false, enable_self_rating: true, enable_supervisor_rating: true,
  primary_source: "supervisor", rating_scale: "0-10",
  target_type: "percentage", measurement: "higher_is_better", data_source: "manual",
  default_target: null, active: true,
});
const emptySub = () => ({ id: uid(), name: "", weight: 0, kras: [emptyKRA()], active: true });

const sum = (arr) => arr.reduce((s, x) => s + Number(x.weight || 0), 0);
const ok100 = (arr) => arr.length === 0 || Math.abs(sum(arr) - 100) < 0.01;
const balance = (arr) => {
  if (arr.length === 0) return arr;
  const each = Math.floor((100 / arr.length) * 100) / 100;
  const out = arr.map((x) => ({ ...x, weight: each }));
  const s = sum(out);
  out[out.length - 1].weight = Number((out[out.length - 1].weight + (100 - s)).toFixed(2));
  return out;
};

function WeightBadge({ items, label = "Total" }) {
  const good = ok100(items);
  return (
    <Badge className={good ? "bg-[#16A34A]/10 text-[#16A34A] border-0" : "bg-[#D97706]/10 text-[#D97706] border-0"}>
      {good ? <CheckCircle size={12} className="mr-1" /> : <Warning size={12} className="mr-1" />}
      {label}: {sum(items).toFixed(2)}%
    </Badge>
  );
}

/* ---------------- KRA editor ---------------- */
function KRAEditor({ kra, idx, onChange, onRemove }) {
  const set = (patch) => onChange({ ...kra, ...patch });
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white p-3 space-y-2.5" data-testid={`kra-row-${idx}`}>
      <div className="flex gap-2 items-start">
        <div className="flex-1">
          <Label className="small-label">KRA Statement</Label>
          <Input className="mt-1.5" placeholder="e.g. Resolve customer tickets within SLA" value={kra.statement}
                 data-testid={`kra-statement-${idx}`}
                 onChange={(e) => set({ statement: e.target.value })} />
        </div>
        <div className="w-24">
          <Label className="small-label">Weight %</Label>
          <Input type="number" min="0" max="100" className="mt-1.5" value={kra.weight}
                 data-testid={`kra-weight-${idx}`}
                 onChange={(e) => set({ weight: Number(e.target.value) })} />
        </div>
        <Button type="button" variant="ghost" size="sm" className="mt-6 text-[#DC2626]" onClick={onRemove} data-testid={`kra-remove-${idx}`}>
          <Trash size={14} />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={kra.enable_supervisor_rating} data-testid={`kra-sup-${idx}`}
                 onChange={(e) => set({ enable_supervisor_rating: e.target.checked })} /> Manager
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={kra.enable_self_rating} data-testid={`kra-self-${idx}`}
                 onChange={(e) => set({ enable_self_rating: e.target.checked })} /> Employee
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={kra.enable_objective} data-testid={`kra-obj-${idx}`}
                 onChange={(e) => set({ enable_objective: e.target.checked })} /> Objective
        </label>
        <div className="flex items-center gap-1.5">
          <span className="text-[#475569]">Scale</span>
          <Select value={kra.rating_scale} onValueChange={(v) => set({ rating_scale: v })}>
            <SelectTrigger className="h-7 w-20" data-testid={`kra-scale-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="0-10">0–10</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#475569]">Official score</span>
          <Select value={kra.primary_source} onValueChange={(v) => set({ primary_source: v })}>
            <SelectTrigger className="h-7 w-36" data-testid={`kra-primary-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="supervisor">Manager Rating</SelectItem>
              <SelectItem value="objective">Objective Metric</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {kra.enable_objective && (
          <div className="flex items-center gap-1.5">
            <span className="text-[#475569]">Measure</span>
            <Select value={kra.measurement} onValueChange={(v) => set({ measurement: v })}>
              <SelectTrigger className="h-7 w-40" data-testid={`kra-measure-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>{MEASUREMENTS.filter((m) => m.value !== "manager_rating").map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Subcategory editor ---------------- */
function SubcategoryEditor({ sub, idx, onChange, onRemove }) {
  const set = (patch) => onChange({ ...sub, ...patch });
  const setKra = (i, k) => set({ kras: sub.kras.map((x, j) => (j === i ? k : x)) });
  const addKra = () => set({ kras: [...sub.kras, emptyKRA()] });
  const rmKra = (i) => set({ kras: sub.kras.filter((_, j) => j !== i) });
  return (
    <div className="rounded-xl border border-[#CBD5E1] bg-[#F8FAFC] p-3 space-y-3" data-testid={`subcategory-${idx}`}>
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Label className="small-label">Subcategory</Label>
          <Input className="mt-1.5 bg-white" placeholder="e.g. Quantity" value={sub.name}
                 data-testid={`subcategory-name-${idx}`}
                 onChange={(e) => set({ name: e.target.value })} />
        </div>
        <div className="w-24">
          <Label className="small-label">Weight %</Label>
          <Input type="number" min="0" max="100" className="mt-1.5 bg-white" value={sub.weight}
                 data-testid={`subcategory-weight-${idx}`}
                 onChange={(e) => set({ weight: Number(e.target.value) })} />
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-[#DC2626]" onClick={onRemove} data-testid={`subcategory-remove-${idx}`}>
          <Trash size={14} />
        </Button>
      </div>

      <div className="pl-2 border-l-2 border-[#0EA5E9]/30 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="small-label">KRAs</span>
            <WeightBadge items={sub.kras} label="KRA" />
          </div>
          <div className="flex gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => set({ kras: balance(sub.kras) })}>
              <Copy size={13} className="mr-1" /> Balance
            </Button>
            <Button type="button" size="sm" onClick={addKra} data-testid={`add-kra-${idx}`} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              <Plus size={13} className="mr-1" /> KRA
            </Button>
          </div>
        </div>
        {sub.kras.map((k, i) => (
          <KRAEditor key={k.id} kra={k} idx={`${idx}-${i}`} onChange={(nk) => setKra(i, nk)} onRemove={() => rmKra(i)} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Subcategory list (per KPI) ---------------- */
function SubcategoryList({ line, onChange }) {
  const subs = line.subcategories || [];
  const setSubs = (next) => onChange({ subcategories: next });
  const setSub = (i, s) => setSubs(subs.map((x, j) => (j === i ? s : x)));
  return (
    <div className="rounded-xl border border-dashed border-[#0EA5E9]/40 bg-[#0EA5E9]/[0.03] p-3 space-y-3" data-testid={`subcategory-list-${line.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TreeStructure size={16} weight="duotone" color="#0EA5E9" />
          <span className="small-label">Subcategories</span>
          <WeightBadge items={subs} label="Sub" />
        </div>
        <div className="flex gap-1.5">
          <Button type="button" variant="outline" size="sm" onClick={() => setSubs(balance(subs))}>
            <Copy size={13} className="mr-1" /> Balance
          </Button>
          <Button type="button" size="sm" onClick={() => setSubs([...subs, emptySub()])} data-testid={`add-subcategory-${line.id}`} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
            <Plus size={13} className="mr-1" /> Subcategory
          </Button>
        </div>
      </div>
      {subs.length === 0 && <div className="text-xs text-[#94A3B8] py-2">No subcategories yet. Add one to define KRAs.</div>}
      {subs.map((s, i) => (
        <SubcategoryEditor key={s.id} sub={s} idx={i} onChange={(ns) => setSub(i, ns)} onRemove={() => setSubs(subs.filter((_, j) => j !== i))} />
      ))}
    </div>
  );
}

export default function KPITemplates() {
  const [list, setList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", position_id: "", description: "", lines: [], active: true, advanced_mode: false });
  const [samples, setSamples] = useState([]);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [applying, setApplying] = useState(null);

  const reload = async () => {
    const [t, p] = await Promise.all([api.get("/kpi-templates"), api.get("/positions")]);
    setList(t.data); setPositions(p.data);
  };
  useEffect(() => { reload(); }, []);

  const openSamples = async () => {
    setSampleOpen(true);
    if (samples.length === 0) {
      try {
        const { data } = await api.get("/kpi-templates/samples");
        setSamples(data);
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const applySample = async (key) => {
    setApplying(key);
    try {
      await api.post(`/kpi-templates/samples/${key}`);
      toast.success("Sample template added — edit it anytime");
      setSampleOpen(false);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setApplying(null); }
  };

  const total = useMemo(() => sum(form.lines), [form.lines]);
  const totalOk = Math.abs(total - 100) < 0.01;

  const openCreate = () => {
    setEditId(null);
    setForm({ name: "", position_id: positions[0]?.id || "", description: "", lines: [], active: true, advanced_mode: false });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ advanced_mode: false, ...t, lines: (t.lines || []).map((l) => ({ subcategories: [], has_subcategories: false, ...l })) });
    setOpen(true);
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const updateLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm({ ...form, lines });
  };
  const removeLine = (idx) => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });

  const autoBalance = () => setForm({ ...form, lines: balance(form.lines) });

  const validateAdvanced = () => {
    for (const l of form.lines) {
      if (!l.has_subcategories) continue;
      if (!l.subcategories?.length) return `KPI "${l.name || "Unnamed"}" has subcategories enabled but none defined`;
      if (!ok100(l.subcategories)) return `Subcategory weights for "${l.name}" must total 100%`;
      for (const s of l.subcategories) {
        if (!s.kras?.length) return `Subcategory "${s.name || "Unnamed"}" needs at least one KRA`;
        if (!ok100(s.kras)) return `KRA weights for "${s.name}" must total 100%`;
        for (const k of s.kras) {
          if (!k.statement?.trim()) return `Every KRA needs a statement`;
          const enabled = [k.enable_objective && "objective", k.enable_self_rating && "self", k.enable_supervisor_rating && "supervisor"].filter(Boolean);
          if (!enabled.length) return `KRA "${k.statement}" must enable at least one rating source`;
          if (!enabled.includes(k.primary_source)) return `KRA "${k.statement}": official score source must be enabled`;
        }
      }
    }
    return null;
  };

  const save = async () => {
    if (!form.name || !form.position_id) return toast.error("Name and Position are required");
    if (!totalOk) return toast.error(`KPI weights must total 100%. Currently ${total.toFixed(2)}%`);
    if (form.lines.some((l) => !l.name?.trim())) return toast.error("All KPI lines need a name");
    if (form.advanced_mode) {
      const err = validateAdvanced();
      if (err) return toast.error(err);
    }
    try {
      if (editId) await api.put(`/kpi-templates/${editId}`, form);
      else await api.post("/kpi-templates", form);
      toast.success("Template saved");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this template?")) return;
    await api.delete(`/kpi-templates/${id}`);
    reload();
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Performance Setup</div>
          <h1 className="font-display text-3xl font-semibold mt-1">KPI Templates</h1>
          <p className="text-[#475569] text-sm mt-1">Define what "great" looks like, per role.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="use-sample-button" onClick={openSamples} variant="outline">
            <Sparkle size={18} className="mr-1" /> Use Sample
          </Button>
          <Button data-testid="new-template-button" onClick={openCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
            <Plus size={18} className="mr-1" /> New Template
          </Button>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState
          testId="templates-empty-state"
          icon={ListChecks}
          title="No KPI templates yet"
          description="A KPI template defines what success looks like for a role. Build one from scratch or start from a ready-made sample."
          actions={
            <>
              <Button data-testid="empty-create-template-button" onClick={openCreate} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                <Plus size={18} className="mr-1" /> Create Template
              </Button>
              <Button data-testid="empty-use-sample-button" onClick={openSamples} variant="outline">
                <Sparkle size={18} className="mr-1" /> Use Sample Template
              </Button>
            </>
          }
        />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((t) => {
          const tot = (t.total_weight || 0).toFixed(0);
          const good = Math.abs(t.total_weight - 100) < 0.01;
          const kraCount = (t.lines || []).reduce((a, l) => a + (l.subcategories || []).reduce((b, s) => b + (s.kras?.length || 0), 0), 0);
          return (
            <Card key={t.id} className="surface-card border-[#E2E8F0]" data-testid={`template-card-${t.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-semibold flex items-center gap-2">
                      <ListChecks size={18} weight="duotone" color="#0EA5E9" /> {t.name}
                    </div>
                    <div className="text-xs text-[#475569] mt-1">{t.position_name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge className={good ? "bg-[#16A34A]/10 text-[#16A34A] border-0" : "bg-[#D97706]/10 text-[#D97706] border-0"}>
                      {good ? <CheckCircle size={12} className="mr-1" /> : <Warning size={12} className="mr-1" />}
                      {tot}%
                    </Badge>
                    {t.advanced_mode && (
                      <Badge className="bg-[#0EA5E9]/10 text-[#0EA5E9] border-0" data-testid={`template-advanced-badge-${t.id}`}>
                        <TreeStructure size={12} className="mr-1" /> Advanced · {kraCount} KRA
                      </Badge>
                    )}
                  </div>
                </div>
                {t.description && <p className="text-sm text-[#475569] mt-3 line-clamp-2">{t.description}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.lines.slice(0, 4).map((l) => (
                    <span key={l.id} className="pill">{l.name} · {l.weight}%{l.has_subcategories ? ` · ${l.subcategories?.length || 0} sub` : ""}</span>
                  ))}
                  {t.lines.length > 4 && <span className="pill">+{t.lines.length - 4} more</span>}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                    <PencilSimple size={14} className="mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash size={14} className="mr-1 text-[#DC2626]" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sample-templates-dialog">
          <DialogHeader>
            <DialogTitle>Start from a sample</DialogTitle>
            <DialogDescription>
              Pick a starter template. We'll add it (and a matching position) to your workspace — you can edit everything afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {samples.length === 0 && <div className="text-sm text-[#475569] py-6 text-center">Loading samples…</div>}
            {samples.map((s) => (
              <div key={s.key} className="p-4 rounded-xl border border-[#E2E8F0] hover:border-[#0EA5E9]/40 transition-colors"
                   data-testid={`sample-card-${s.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold flex items-center gap-2">
                      <ListChecks size={18} weight="duotone" color="#0EA5E9" /> {s.name}
                    </div>
                    <div className="text-xs text-[#475569] mt-0.5">{s.department} · {s.kpi_count} KPIs</div>
                    <p className="text-sm text-[#475569] mt-1.5">{s.description}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.kpis.map((k) => (
                        <span key={k.name} className="pill">{k.name} · {k.weight}%</span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => applySample(s.key)} disabled={applying === s.key}
                          className="bg-[#0EA5E9] hover:bg-[#0284C7] shrink-0"
                          data-testid={`apply-sample-${s.key}`}>
                    {applying === s.key ? "Adding…" : <>Use <ArrowRight size={14} className="ml-1" /></>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit template" : "New KPI template"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="small-label">Template Name</Label>
              <Input data-testid="template-name-input" className="mt-2" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Position</Label>
              <Select value={form.position_id} onValueChange={(v) => setForm({ ...form, position_id: v })}>
                <SelectTrigger className="mt-2" data-testid="template-position-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3">
              <Label className="small-label">Description</Label>
              <Textarea className="mt-2" rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>

          {/* Advanced mode toggle */}
          <div className="flex items-start justify-between gap-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <div>
              <div className="flex items-center gap-2 font-medium text-sm">
                <TreeStructure size={16} weight="duotone" color="#0EA5E9" /> Advanced KPI/KRA Mode
              </div>
              <p className="text-xs text-[#475569] mt-1 max-w-xl">
                Off keeps the standard flat KPIs. On lets each KPI optionally break into Subcategories → KRA statements
                with Self &amp; Supervisor ratings. Simple KPIs (e.g. Attendance) can stay flat.
              </p>
            </div>
            <Switch checked={form.advanced_mode} onCheckedChange={(v) => setForm({ ...form, advanced_mode: v })} data-testid="advanced-mode-switch" />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="small-label">KPI Lines</div>
                <WeightBadge items={form.lines} />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={autoBalance}>
                  <Copy size={14} className="mr-1" /> Auto-balance
                </Button>
                <Button type="button" size="sm" onClick={addLine} data-testid="add-kpi-line-button"
                        className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                  <Plus size={14} className="mr-1" /> Add KPI
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {form.lines.map((l, idx) => (
                <Card key={l.id} className="surface-card border-[#E2E8F0]">
                  <CardContent className="p-4 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                      <div className="sm:col-span-5">
                        <Label className="small-label">Name</Label>
                        <Input data-testid={`kpi-line-name-${idx}`} className="mt-1.5" value={l.name}
                               onChange={(e) => updateLine(idx, { name: e.target.value })} />
                      </div>
                      <div className="sm:col-span-4">
                        <Label className="small-label">Category</Label>
                        <Input className="mt-1.5" value={l.category}
                               onChange={(e) => updateLine(idx, { category: e.target.value })} />
                      </div>
                      <div className="sm:col-span-3">
                        <Label className="small-label">Weight %</Label>
                        <Input data-testid={`kpi-line-weight-${idx}`} type="number" min="0" max="100" className="mt-1.5"
                               value={l.weight}
                               onChange={(e) => updateLine(idx, { weight: Number(e.target.value) })} />
                      </div>

                      {/* Flat config — hidden when this KPI uses subcategories */}
                      {!(form.advanced_mode && l.has_subcategories) && (
                        <>
                          <div className="sm:col-span-3">
                            <Label className="small-label">Type</Label>
                            <Select value={l.kpi_type} onValueChange={(v) => updateLine(idx, {
                              kpi_type: v,
                              measurement: v === "manager_rated" ? "manager_rating" : (l.measurement === "manager_rating" ? "higher_is_better" : l.measurement),
                              target_type: v === "manager_rated" ? "rating_scale" : l.target_type,
                            })}>
                              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>{KPI_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-3">
                            <Label className="small-label">Target Type</Label>
                            <Select value={l.target_type} onValueChange={(v) => updateLine(idx, { target_type: v })}>
                              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-3">
                            <Label className="small-label">Measurement</Label>
                            <Select value={l.measurement} onValueChange={(v) => updateLine(idx, { measurement: v })}>
                              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>{MEASUREMENTS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-3">
                            <Label className="small-label">Data Source</Label>
                            <Select value={l.data_source} onValueChange={(v) => updateLine(idx, { data_source: v })}>
                              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                              <SelectContent>{DATA_SOURCES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-12">
                            <Label className="small-label">Description</Label>
                            <Input className="mt-1.5" value={l.description}
                                   onChange={(e) => updateLine(idx, { description: e.target.value })} />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Per-KPI subcategory toggle (advanced mode only) */}
                    {form.advanced_mode && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer pt-1" data-testid={`kpi-subcat-toggle-${idx}`}>
                        <Switch checked={!!l.has_subcategories}
                                onCheckedChange={(v) => updateLine(idx, { has_subcategories: v, subcategories: v && !(l.subcategories?.length) ? [emptySub()] : (l.subcategories || []) })} />
                        Break this KPI into Subcategories → KRAs
                      </label>
                    )}

                    {form.advanced_mode && l.has_subcategories && (
                      <SubcategoryList line={l} onChange={(patch) => updateLine(idx, patch)} />
                    )}

                    <div className="flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)} data-testid={`remove-kpi-line-${idx}`}>
                        <Trash size={14} className="mr-1 text-[#DC2626]" /> Remove
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-template-button" onClick={save} disabled={!totalOk}
                    className="bg-[#0EA5E9] hover:bg-[#0284C7]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
